import { useState, useEffect, useMemo } from "react";
import { newCartId, withCartIds } from "@/lib/cartId";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { BrandLockup } from "@/components/BrandMark";
import ThemeToggle from "@/components/ThemeToggle";
import { StockBadge } from "@/components/StockBadge";
import useStockStatus from "@/hooks/useStockStatus";
import { Input } from "@/components/ui/input";
import { americanCrepeCharts, getCrepeChartUrl } from "@/data/galleryImages";
import {
  ArrowLeft,
  ZoomIn,
  X,
  Plus,
  Trash2,
  MessageCircle,
  ShoppingCart,
  Info,
} from "lucide-react";

const ADMIN_WA_NUMBER = "917624853840";
const UNIT_LABELS = { than: "Than", bundle: "Bundle" };
const UNIT_METERS = { than: 24, bundle: 24 };

const buildWaLink = (items, customerName, notes) => {
  const lines = [
    "*American Crepe Order — Mutha Paras & Co.*",
    "",
    customerName ? `*Customer:* ${customerName}` : null,
    "",
    "*Shades & Quantities:*",
    ...items.map(
      (it, i) =>
        `${i + 1}. Shade #${it.shade} — ${it.quantity} ${UNIT_LABELS[it.unitType]}(s) (${it.meters} m)`
    ),
    "",
    `*Total Lines:* ${items.length}`,
    `*Total Fabric Ordered:* ${items.reduce((s, it) => s + Number(it.meters || 0), 0)} m`,
    "",
    notes ? `*Notes:* ${notes}` : null,
    "",
    "_Sent via muthaparas.com — American Crepe catalogue_",
  ].filter(Boolean);
  return `https://wa.me/${ADMIN_WA_NUMBER}?text=${encodeURIComponent(lines.join("\n"))}`;
};

export default function AmericanCrepeCatalogue() {
  const stock = useStockStatus();
  const [shadeInput, setShadeInput] = useState("");
  const [quantityInput, setQuantityInput] = useState("");
  const [unitType, setUnitType] = useState("than");
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState(() => {
    try {
      const saved = localStorage.getItem("crepe_cart");
      return saved ? withCartIds(JSON.parse(saved)) : [];
    } catch (err) {
      console.warn("Failed to restore crepe cart from localStorage:", err);
      return [];
    }
  });
  const [lightbox, setLightbox] = useState(null);
  const [error, setError] = useState("");

  // Persist cart locally
  useEffect(() => {
    localStorage.setItem("crepe_cart", JSON.stringify(items));
  }, [items]);

  const charts = useMemo(
    () => americanCrepeCharts.map((c, idx) => ({ ...c, ...getCrepeChartUrl(c.file), idx })),
    []
  );

  const totalMeters = items.reduce((s, it) => s + Number(it.meters || 0), 0);

  const handleAdd = (e) => {
    e?.preventDefault();
    setError("");
    const shade = shadeInput.trim();
    const quantity = Number(quantityInput);
    if (!shade) return setError("Please enter a shade number.");
    if (!quantity || quantity <= 0) return setError("Please enter a quantity (>0).");
    const meters = quantity * UNIT_METERS[unitType];
    setItems((prev) => [...prev, { id: newCartId(), shade, quantity, unitType, meters }]);
    setShadeInput("");
    setQuantityInput("");
  };

  const handleRemove = (id) =>
    setItems((prev) => prev.filter((it) => it.id !== id));

  const handleClear = () => setItems([]);

  const waLink = items.length > 0 ? buildWaLink(items, name, notes) : null;

  return (
    <div
      className="min-h-screen pb-32"
      style={{ backgroundColor: "var(--mp-paper)" }}
      data-testid="american-crepe-catalogue"
    >
      {/* Header */}
      <div className="border-b border-[var(--mp-line)] bg-white sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link
            to="/product/american-crepe"
            className="flex items-center gap-2 text-sm hover:opacity-70 transition"
            style={{ color: "var(--mp-text-strong)", fontFamily: "'Manrope', sans-serif" }}
            data-testid="crepe-back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to American Crepe
          </Link>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <Link
              to="/"
              className="text-sm font-medium hidden sm:block"
              style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-text-strong)" }}
            >
              <BrandLockup />
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
        {/* Title */}
        <div className="mb-10 max-w-3xl">
          <p
            className="text-xs uppercase tracking-[0.2em] font-semibold mb-3"
            style={{ color: "var(--mp-copper)", fontFamily: "'Manrope', sans-serif" }}
          >
            American Crepe — Full Catalogue
          </p>
          <h1
            className="text-4xl md:text-5xl tracking-tight leading-none font-medium mb-4"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
          >
            Pick by Shade Number
          </h1>
          <div className="mb-4">
            <StockBadge status={stock["american-crepe"] || "ready"} size="md" />
          </div>
          <p
            className="text-base leading-relaxed"
            style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}
          >
            Browse {charts.length} swatch charts. Tap any chart to zoom in, note the shade number,
            then add it to your order using the panel below. Send to our WhatsApp when ready.
          </p>
        </div>

        {/* How to order */}
        <div
          className="mb-10 p-5 border border-[var(--mp-line)] rounded-sm flex items-start gap-3 pastel-cream"
          data-testid="how-to-order"
        >
          <Info className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: "var(--mp-text-strong)" }} />
          <div>
            <p className="text-sm font-medium mb-1" style={{ color: "var(--mp-text)", fontFamily: "'Manrope', sans-serif" }}>
              How to order
            </p>
            <ol
              className="text-xs leading-relaxed list-decimal pl-4 space-y-0.5"
              style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}
            >
              <li>Tap any chart below to view it full-screen and read the shade number on each swatch.</li>
              <li>American Crepe is big panna, so one than is <strong>24m</strong>. Type the shade number and how many thans you need.</li>
              <li>Click <strong>Add</strong> to keep building the list.</li>
              <li>Hit <strong>Send via WhatsApp</strong> — your order opens with all details pre-filled.</li>
            </ol>
          </div>
        </div>

        {/* Charts grid */}
        <div
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 mb-12"
          data-testid="crepe-chart-grid"
        >
          {charts.map((c) => (
            <button
              key={c.file}
              onClick={() => setLightbox(c)}
              className="group relative border border-[var(--mp-line)] rounded-sm overflow-hidden cursor-zoom-in bg-white"
              data-testid={`crepe-chart-${c.idx + 1}`}
            >
              <img
                src={c.thumb}
                alt={c.label}
                loading="lazy"
                className="w-full h-44 sm:h-56 object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div
                className="absolute inset-0 flex items-end justify-between p-2 sm:p-3 opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ background: "linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 50%)" }}
              >
                <span
                  className="text-xs text-white font-medium"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  {c.label}
                </span>
                <ZoomIn className="w-4 h-4 text-white" />
              </div>
              <div
                className="absolute top-2 left-2 px-2 py-1 rounded-sm text-[10px] font-semibold"
                style={{ backgroundColor: "rgba(255,255,255,0.95)", color: "var(--mp-text-strong)", fontFamily: "'Manrope', sans-serif" }}
              >
                Chart {c.idx + 1}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0,0,0,0.92)" }}
          onClick={() => setLightbox(null)}
          data-testid="crepe-lightbox"
        >
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 sm:top-6 sm:right-6 p-2 rounded-full bg-white/10 hover:bg-white/20 transition"
            aria-label="Close"
            data-testid="crepe-lightbox-close"
          >
            <X className="w-5 h-5 text-white" />
          </button>
          <img
            src={lightbox.url}
            alt={lightbox.label}
            className="max-w-full max-h-[88vh] object-contain rounded-sm"
            onClick={(e) => e.stopPropagation()}
          />
          <p
            className="absolute bottom-4 left-1/2 -translate-x-1/2 text-xs sm:text-sm text-white/85 px-4 text-center"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            {lightbox.label} — pinch / scroll to zoom in for clear shade numbers
          </p>
        </div>
      )}

      {/* Sticky Order Pad */}
      <div
        className="fixed bottom-0 left-0 right-0 z-40 border-t border-[var(--mp-line)] bg-white shadow-[0_-8px_24px_rgba(0,0,0,0.06)]"
        data-testid="crepe-order-pad"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-3 sm:py-4">
          <div className="flex flex-col lg:flex-row lg:items-end gap-3 lg:gap-5">
            {/* Add row */}
            <form
              onSubmit={handleAdd}
              className="flex flex-1 flex-wrap items-end gap-2"
              data-testid="crepe-add-form"
            >
              <div className="flex-1 min-w-[110px]">
                <label
                  className="text-[10px] uppercase tracking-wider font-medium mb-1 block"
                  style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}
                >
                  Shade #
                </label>
                <Input
                  value={shadeInput}
                  onChange={(e) => setShadeInput(e.target.value)}
                  placeholder="e.g. 547"
                  inputMode="numeric"
                  className="rounded-sm border-[var(--mp-line)] py-4 text-sm"
                  data-testid="crepe-shade-input"
                />
              </div>
              <div className="w-24 sm:w-28 flex-shrink-0">
                <label
                  className="text-[10px] uppercase tracking-wider font-medium mb-1 block"
                  style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}
                >
                  Quantity
                </label>
                <Input
                  value={quantityInput}
                  onChange={(e) => setQuantityInput(e.target.value)}
                  placeholder="5"
                  type="number"
                  min="1"
                  inputMode="numeric"
                  className="rounded-sm border-[var(--mp-line)] py-4 text-sm"
                  data-testid="crepe-quantity-input"
                />
              </div>
              <div className="flex flex-col flex-shrink-0">
                <label
                  className="text-[10px] uppercase tracking-wider font-medium mb-1 block"
                  style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}
                >
                  Unit
                </label>
                <div
                  className="inline-flex rounded-sm border border-[var(--mp-line)] overflow-hidden"
                  data-testid="crepe-unit-toggle"
                  role="tablist"
                  aria-label="Quantity unit"
                >
                  <button
                    type="button"
                    onClick={() => setUnitType("than")}
                    className="px-3 py-3 text-xs font-medium transition"
                    style={{
                      backgroundColor: unitType === "than" ? "#101010" : "transparent",
                      color: unitType === "than" ? "var(--mp-on-solid)" : "var(--mp-muted)",
                      fontFamily: "'Manrope', sans-serif",
                    }}
                    data-testid="crepe-unit-than"
                    aria-pressed={unitType === "than"}
                  >
                    Than <span className="opacity-70">(24m)</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setUnitType("bundle")}
                    className="px-3 py-3 text-xs font-medium border-l border-[var(--mp-line)] transition"
                    style={{
                      backgroundColor: unitType === "bundle" ? "#101010" : "transparent",
                      color: unitType === "bundle" ? "var(--mp-on-solid)" : "var(--mp-muted)",
                      fontFamily: "'Manrope', sans-serif",
                    }}
                    data-testid="crepe-unit-bundle"
                    aria-pressed={unitType === "bundle"}
                  >
                    Bundle <span className="opacity-70">(24m)</span>
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                className="rounded-sm py-4 px-4 text-white text-sm flex-shrink-0"
                style={{ backgroundColor: "var(--mp-solid)", fontFamily: "'Manrope', sans-serif" }}
                data-testid="crepe-add-btn"
              >
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </form>

            {/* Summary */}
            <div className="flex items-center gap-3 lg:gap-5 lg:border-l lg:border-[var(--mp-line)] lg:pl-5">
              <div className="text-right">
                <p
                  className="text-[10px] uppercase tracking-wider"
                  style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}
                >
                  Order
                </p>
                <p
                  className="text-sm font-semibold"
                  style={{ color: "var(--mp-text)", fontFamily: "'Manrope', sans-serif" }}
                  data-testid="crepe-total"
                >
                  {items.length} {items.length === 1 ? "shade" : "shades"} · {totalMeters} m
                </p>              </div>
              {waLink ? (
                <a
                  href={waLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="crepe-send-wa-btn"
                >
                  <Button
                    className="rounded-sm py-4 px-4 sm:px-6 text-white text-sm whitespace-nowrap whatsapp-pulse"
                    style={{ backgroundColor: "#25D366", fontFamily: "'Manrope', sans-serif" }}
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Send via WhatsApp
                  </Button>
                </a>
              ) : (
                <Button
                  disabled
                  className="rounded-sm py-4 px-4 sm:px-6 text-white text-sm whitespace-nowrap opacity-50 cursor-not-allowed"
                  style={{ backgroundColor: "#25D366", fontFamily: "'Manrope', sans-serif" }}
                  data-testid="crepe-send-wa-btn-disabled"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Send via WhatsApp
                </Button>
              )}
            </div>
          </div>

          {/* Error */}
          {error && (
            <p
              className="text-xs mt-2"
              style={{ color: "#b91c1c", fontFamily: "'Manrope', sans-serif" }}
              data-testid="crepe-error"
            >
              {error}
            </p>
          )}

          {/* Item list (collapsible mini view) */}
          {items.length > 0 && (
            <div className="mt-3 border-t border-[var(--mp-line)] pt-3">
              <div
                className="flex flex-wrap gap-2 max-h-24 overflow-y-auto"
                data-testid="crepe-item-list"
              >
                {items.map((it, i) => (
                  <div
                    key={it.id}
                    className="flex items-center gap-1 pl-2 pr-1 py-1 rounded-sm border border-[var(--mp-line)] text-xs"
                    style={{ backgroundColor: "var(--mp-paper)", fontFamily: "'Manrope', sans-serif", color: "var(--mp-text)" }}
                  >
                    <span className="font-semibold">#{it.shade}</span>
                    <span style={{ color: "var(--mp-muted)" }}>·</span>
                    <span>{it.quantity} {UNIT_LABELS[it.unitType]}{it.quantity > 1 ? "s" : ""}</span>
                    <span style={{ color: "var(--mp-muted)" }} className="text-[10px]">({it.meters}m)</span>
                    <button
                      onClick={() => handleRemove(it.id)}
                      className="ml-1 p-0.5 hover:bg-red-50 rounded"
                      aria-label={`Remove shade ${it.shade}`}
                      data-testid={`crepe-remove-${i}`}
                    >
                      <X className="w-3 h-3" style={{ color: "#b91c1c" }} />
                    </button>
                  </div>
                ))}
              </div>
              <button
                onClick={handleClear}
                className="text-xs mt-2 inline-flex items-center gap-1 hover:underline"
                style={{ color: "var(--mp-copper)", fontFamily: "'Manrope', sans-serif" }}
                data-testid="crepe-clear-btn"
              >
                <Trash2 className="w-3 h-3" /> Clear all
              </button>
            </div>
          )}

          {/* Optional name + notes */}
          <details className="mt-3 text-xs" style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}>
            <summary className="cursor-pointer inline-flex items-center gap-1">
              <ShoppingCart className="w-3 h-3" />
              Add your name &amp; notes (optional)
            </summary>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name / company"
                className="rounded-sm border-[var(--mp-line)] py-3 text-sm"
                data-testid="crepe-name-input"
              />
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Notes (delivery city, special instructions)"
                className="rounded-sm border-[var(--mp-line)] py-3 text-sm"
                data-testid="crepe-notes-input"
              />
            </div>
          </details>
        </div>
      </div>
    </div>
  );
}
